import { createAuditEvent } from "../db/audit";
import type { CEOOSDatabase } from "../db/database";
import { createEntityMetadata, INTERNAL_SCHEMA_VERSION } from "../domain/common";
import { shiftDateKey, zonedDateTimeToIso } from "../domain/business-date";
import {
  createBehaviorCorrection,
  createEvidenceStatement,
  createPrivateSourceRecord,
} from "../domain/factories";
import { createEmptyReviewDraft, parseReviewDraft, type ReviewDraftData } from "../domain/review";
import type {
  BehaviorCorrection,
  BoardContextImportPayload,
  BusinessContextSnapshot,
  CEOProfile,
  CEOReviewExport,
  ConstitutionVersion,
  CorrectionVerificationResult,
  DailyCEOReview,
  DataExportManifest,
  EvidenceStatement,
  ExternalSummaryRecord,
  ReviewMode,
  StructuredCEORecord,
} from "../domain/types";
import {
  projectCEOReviewExport,
  type BoardSummaryDraft,
  type ReviewDeviationField,
} from "../import-export/board-export";
import { assertNoIssues, DomainInvariantError, RuleErrorCode } from "../rules/errors";
import { validateEvidenceStatement, validateSingleActiveCorrection } from "../rules/invariants";
import { synthesizeReview, type ReviewSynthesisResult } from "../rules/review-synthesis";

const DEFAULT_CONSTITUTION_ID = "00000000-0000-4000-8000-000000000100";

export interface BusinessContextView {
  source: "board_import" | "manual_entry";
  sourceId: string;
  date: string;
  current180DayMainline: string;
  currentConstraint: string;
  todayBusinessBattle: string;
  currentMaximumRisk: string;
  noStartList: string[];
  tomorrowBusinessAction: string;
}

export interface ReviewHistoryItem {
  review: DailyCEOReview;
  correction?: BehaviorCorrection | undefined;
  exported: boolean;
}

function isDailyReview(record: StructuredCEORecord): record is DailyCEOReview {
  return record.recordType === "daily_review";
}

function isCorrection(record: StructuredCEORecord): record is BehaviorCorrection {
  return record.recordType === "behavior_correction";
}

export function createInitialProfile(
  businessTimezone: string,
  now = new Date().toISOString(),
): CEOProfile {
  return {
    ...createEntityMetadata(
      { kind: "user_input", referenceIds: [] },
      { now, status: "draft", exportAllowed: false, deletionPolicy: "retain" },
    ),
    businessStage: "",
    businessTimezone,
    preferredLocale: "zh-CN",
    weekStartDay: 1,
    onboardingStatus: "in_progress",
    onboardingStep: 1,
    defaultFieldMode: "shenzhen",
    ideaCoolingHours: 72,
    governanceExportDefaults: {
      truth: "none",
      focus: "aggregate_only",
      promise: "risk_only",
    },
    currentConstitutionVersionId: DEFAULT_CONSTITUTION_ID,
  };
}

export async function loadOrCreateProfile(
  database: CEOOSDatabase,
  businessTimezone: string,
  now = new Date().toISOString(),
): Promise<CEOProfile> {
  const existing = await database.profiles.toCollection().first();
  if (existing) return existing;
  const profile = createInitialProfile(businessTimezone, now);
  await database.profiles.put(profile);
  return profile;
}

export async function saveProfileProgress(
  database: CEOOSDatabase,
  profile: CEOProfile,
  now = new Date().toISOString(),
): Promise<CEOProfile> {
  const next = { ...profile, updatedAt: now };
  await database.profiles.put(next);
  return next;
}

export async function completeOnboarding(
  database: CEOOSDatabase,
  profile: CEOProfile,
  now = new Date().toISOString(),
): Promise<CEOProfile> {
  const constitution = await database.constitutions.get(profile.currentConstitutionVersionId);
  if (!constitution) throw new Error("CEO-ONBOARDING-001: 找不到待确认的共同宪法。");
  const activeConstitution: ConstitutionVersion = {
    ...constitution,
    status: "active",
    source: { kind: "user_input", referenceIds: [constitution.id] },
    approvedByUserAt: now,
    updatedAt: now,
    principles: constitution.principles.map((principle) => ({
      ...principle,
      status: "active",
      source: { kind: "user_input", referenceIds: [constitution.id] },
      updatedAt: now,
    })),
  };
  const completed: CEOProfile = {
    ...profile,
    status: "active",
    onboardingStatus: "completed",
    onboardingStep: 5,
    constitutionAcknowledgedAt: now,
    privacyAcknowledgedAt: now,
    onboardingCompletedAt: now,
    updatedAt: now,
  };
  await database.transaction(
    "rw",
    database.profiles,
    database.constitutions,
    database.auditEvents,
    async () => {
      await database.profiles.put(completed);
      await database.constitutions.put(activeConstitution);
      await database.auditEvents.bulkPut([
        createAuditEvent({
          entityType: "CEOProfile",
          entityId: completed.id,
          action: "update",
          actor: "user",
          reason: "用户完成最低必要 Onboarding 与隐私确认。",
          changedFields: [
            "onboardingStatus",
            "constitutionAcknowledgedAt",
            "privacyAcknowledgedAt",
          ],
          now,
        }),
        createAuditEvent({
          entityType: "ConstitutionVersion",
          entityId: activeConstitution.id,
          action: "update",
          actor: "user",
          reason: "用户明确确认共同宪法当前版本。",
          changedFields: ["status", "approvedByUserAt"],
          now,
        }),
      ]);
    },
  );
  return completed;
}

export async function saveManualBusinessContext(
  database: CEOOSDatabase,
  profileId: string,
  dateKey: string,
  input: Omit<
    BusinessContextSnapshot,
    | keyof ReturnType<typeof createEntityMetadata>
    | "recordType"
    | "effectiveDate"
    | "sourceRecordIds"
    | "userConfirmation"
  >,
  now = new Date().toISOString(),
  options: { audit?: boolean } = {},
): Promise<BusinessContextSnapshot> {
  const existing = (
    await database.structuredRecords
      .where("[recordType+effectiveDate]")
      .equals(["business_context", dateKey])
      .toArray()
  ).find(
    (record): record is BusinessContextSnapshot =>
      record.recordType === "business_context" &&
      record.source.kind === "user_input" &&
      record.deletionState === "active",
  );
  const context: BusinessContextSnapshot = {
    ...(existing ??
      createEntityMetadata(
        { kind: "user_input", referenceIds: [profileId] },
        { now, status: "active", exportAllowed: false },
      )),
    recordType: "business_context",
    effectiveDate: dateKey,
    sourceRecordIds: [],
    userConfirmation: "confirmed",
    ...input,
    updatedAt: now,
  };
  if (options.audit === false) {
    await database.structuredRecords.put(context);
  } else {
    await database.transaction("rw", database.structuredRecords, database.auditEvents, async () => {
      await database.structuredRecords.put(context);
      await database.auditEvents.put(
        createAuditEvent({
          entityType: "business_context",
          entityId: context.id,
          action: existing ? "update" : "create",
          actor: "user",
          reason: "用户手动保存最小业务上下文；来源未标记为董事会导入。",
          changedFields: existing ? Object.keys(input) : [],
          now,
        }),
      );
    });
  }
  return context;
}

export async function getCurrentBusinessContext(
  database: CEOOSDatabase,
): Promise<BusinessContextView | undefined> {
  const board = await database.boardImports.where("status").equals("active").toArray();
  const activeBoard = board.sort((left, right) =>
    right.importedAt.localeCompare(left.importedAt),
  )[0];
  if (activeBoard) {
    const payload: BoardContextImportPayload = activeBoard.envelope.payload;
    return {
      source: "board_import",
      sourceId: activeBoard.id,
      date: payload.date,
      current180DayMainline: payload.current_180_day_mainline,
      currentConstraint: payload.current_constraint,
      todayBusinessBattle: payload.today_business_battle,
      currentMaximumRisk: payload.current_maximum_risk,
      noStartList: payload.no_start_list,
      tomorrowBusinessAction: payload.tomorrow_business_action,
    };
  }
  const manual = (
    await database.structuredRecords.where("recordType").equals("business_context").toArray()
  )
    .filter(
      (record): record is BusinessContextSnapshot =>
        record.recordType === "business_context" &&
        record.source.kind === "user_input" &&
        record.deletionState === "active" &&
        Boolean(
          record.current180DayMainline.trim() ||
          record.currentConstraint.trim() ||
          record.todayBusinessBattle.trim() ||
          record.currentMaximumRisk.trim() ||
          record.tomorrowBusinessAction.trim() ||
          record.noStartList.length,
        ),
    )
    .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt))[0];
  if (!manual) return undefined;
  return {
    source: "manual_entry",
    sourceId: manual.id,
    date: manual.effectiveDate,
    current180DayMainline: manual.current180DayMainline,
    currentConstraint: manual.currentConstraint,
    todayBusinessBattle: manual.todayBusinessBattle,
    currentMaximumRisk: manual.currentMaximumRisk,
    noStartList: manual.noStartList,
    tomorrowBusinessAction: manual.tomorrowBusinessAction,
  };
}

export async function getReviewForDate(
  database: CEOOSDatabase,
  dateKey: string,
): Promise<DailyCEOReview | undefined> {
  const reviews = (
    await database.structuredRecords
      .where("[recordType+effectiveDate]")
      .equals(["daily_review", dateKey])
      .toArray()
  ).filter(
    (record): record is DailyCEOReview =>
      isDailyReview(record) && record.deletionState === "active",
  );
  if (reviews.length > 1) {
    throw new DomainInvariantError([
      {
        code: RuleErrorCode.ONE_ACTIVE_REVIEW_PER_DAY,
        path: "effectiveDate",
        message: "同一业务日期存在多个当前有效复盘。",
      },
    ]);
  }
  return reviews[0];
}

export async function loadOrCreateReviewDraft(
  database: CEOOSDatabase,
  dateKey: string,
  mode: ReviewMode,
  now = new Date().toISOString(),
): Promise<ReviewDraftData> {
  const review = await getReviewForDate(database, dateKey);
  if (review) {
    const source = await database.privateSources.get(review.privateSourceRecordId);
    if (source) return parseReviewDraft(source.rawText);
  }
  return createEmptyReviewDraft(dateKey, mode, now);
}

export async function saveReviewDraft(
  database: CEOOSDatabase,
  draft: ReviewDraftData,
  now = new Date().toISOString(),
): Promise<ReviewDraftData> {
  const savedDraft = { ...draft, lastSavedAt: now };
  const existingReview = await getReviewForDate(database, draft.dateKey);
  const existingSource = await database.privateSources.get(draft.privateSourceId);
  const privateSource = existingSource
    ? {
        ...existingSource,
        rawText: JSON.stringify(savedDraft),
        occurredAt: now,
        updatedAt: now,
      }
    : createPrivateSourceRecord(JSON.stringify(savedDraft), {
        id: draft.privateSourceId,
        recordType: "daily_review_raw",
        occurredAt: now,
        now,
      });
  const review: DailyCEOReview = {
    ...(existingReview ??
      createEntityMetadata(
        { kind: "user_input", referenceIds: [draft.privateSourceId] },
        {
          id: draft.reviewId,
          now,
          status: "draft",
          exportAllowed: false,
        },
      )),
    recordType: "daily_review",
    effectiveDate: draft.dateKey,
    reviewDate: draft.dateKey,
    reviewMode: draft.mode,
    reviewStatus: "draft",
    draftStep: draft.step,
    privateSourceRecordId: draft.privateSourceId,
    sourceRecordIds: [draft.privateSourceId],
    source: { kind: "user_input", referenceIds: [draft.privateSourceId] },
    userConfirmation: "unconfirmed",
    statementIds: existingReview?.statementIds ?? [],
    updatedAt: now,
  };
  await database.transaction(
    "rw",
    database.privateSources,
    database.structuredRecords,
    async () => {
      await database.privateSources.put(privateSource);
      await database.structuredRecords.put(review);
    },
  );
  return savedDraft;
}

function materializeStatements(
  draft: ReviewDraftData,
  synthesis: ReviewSynthesisResult,
  now: string,
): EvidenceStatement[] {
  const accepted = draft.statements.filter(
    (statement) =>
      statement.userConfirmation === "confirmed" || statement.userConfirmation === "corrected",
  );
  if (accepted.length === 0 || accepted.some((statement) => !statement.content.trim())) {
    throw new DomainInvariantError([
      {
        code: RuleErrorCode.REVIEW_STATEMENT_CONFIRMATION_REQUIRED,
        path: "statements",
        message: "提交前至少确认一条有内容的陈述。",
      },
    ]);
  }
  const factIds = accepted
    .filter((statement) => statement.statementType === "fact")
    .map((statement) => statement.id);
  const basisIds = accepted
    .filter((statement) => statement.statementType !== "recommendation")
    .map((statement) => statement.id);
  return accepted.map((item) => {
    const statement = createEvidenceStatement(
      {
        id: item.id,
        type: item.statementType,
        content: item.content.trim(),
        effectiveDate: draft.dateKey,
        generatedBy: "user",
        sourceRecordIds: [draft.privateSourceId, draft.reviewId],
        userConfirmed: true,
        confidence: synthesis.issueType === "insufficient_data" ? 0 : 0.75,
        supportingFactIds: factIds,
        ...(item.verificationCondition === undefined
          ? {}
          : { verificationCondition: item.verificationCondition }),
        ...(item.falsificationCondition === undefined
          ? {}
          : { falsificationCondition: item.falsificationCondition }),
        basedOnStatementIds: basisIds,
        principleIds: basisIds.length === 0 ? ["00000000-0000-4000-8000-000000000001"] : [],
      },
      now,
    );
    return {
      ...statement,
      userConfirmation: item.userConfirmation,
      status: "active",
      exportAllowed: true,
    };
  });
}

export async function completeReview(
  database: CEOOSDatabase,
  profile: CEOProfile,
  draft: ReviewDraftData,
  now = new Date().toISOString(),
): Promise<{
  review: DailyCEOReview;
  correction: BehaviorCorrection;
  statements: EvidenceStatement[];
}> {
  const synthesis = synthesizeReview(draft.answers, {
    existingStatements: draft.statements,
  });
  assertNoIssues(synthesis.issues);
  if (synthesis.status !== "ready") {
    throw new DomainInvariantError([
      {
        code: RuleErrorCode.REVIEW_BUSINESS_IMPACT_REQUIRED,
        path: "answers.issueType",
        message: "证据不足时请补充信息或明确选择问题类型后再完成复盘。",
      },
    ]);
  }
  const existingReview = await getReviewForDate(database, draft.dateKey);
  if (!existingReview || existingReview.id !== draft.reviewId) {
    throw new Error("CEO-REVIEW-010: 复盘草稿不存在或业务日期冲突。");
  }
  const statements = materializeStatements(draft, synthesis, now);
  const statementMap = new Map(statements.map((statement) => [statement.id, statement]));
  for (const statement of statements) {
    assertNoIssues(validateEvidenceStatement(statement, { statementsById: statementMap }));
  }
  const correction = createBehaviorCorrection(
    {
      effectiveDate: draft.dateKey,
      behaviorToChange: synthesis.founderDeviation,
      action: synthesis.correctionAction,
      affectedBusinessResult: synthesis.affectedBusinessResult,
      deadline: zonedDateTimeToIso(
        shiftDateKey(draft.dateKey, 1),
        "23:59:00",
        profile.businessTimezone,
      ),
      verificationMetric: synthesis.verificationMetric,
      sourceReviewId: draft.reviewId,
    },
    now,
  );
  const sameDayCorrections = (
    await database.structuredRecords
      .where("[recordType+effectiveDate]")
      .equals(["behavior_correction", draft.dateKey])
      .toArray()
  ).filter(isCorrection);
  assertNoIssues(validateSingleActiveCorrection(correction, sameDayCorrections));
  const review: DailyCEOReview = {
    ...existingReview,
    status: "completed",
    userConfirmation: "confirmed",
    exportAllowed: true,
    reviewMode: draft.mode,
    reviewStatus: "completed",
    draftStep: draft.step,
    statementIds: statements.map((statement) => statement.id),
    correctionId: correction.id,
    issueType: synthesis.issueType,
    founderDeviation: synthesis.founderDeviation,
    affectedBusinessResult: synthesis.affectedBusinessResult,
    verificationMethod: synthesis.verificationMetric,
    exportFieldSummaries: synthesis.exportFieldDrafts,
    completedAt: now,
    updatedAt: now,
  };
  await database.transaction("rw", database.structuredRecords, database.auditEvents, async () => {
    await database.structuredRecords.bulkPut([...statements, correction, review]);
    await database.auditEvents.bulkPut([
      ...statements.map((statement) =>
        createAuditEvent({
          entityType: "evidence_statement",
          entityId: statement.id,
          action: "create",
          actor: "user",
          reason: "用户在复盘提交前确认陈述分类。",
          now,
        }),
      ),
      createAuditEvent({
        entityType: "behavior_correction",
        entityId: correction.id,
        action: "create",
        actor: "user",
        reason: "用户明确确认明日唯一行为纠偏。",
        now,
      }),
      createAuditEvent({
        entityType: "daily_review",
        entityId: review.id,
        action: "update",
        actor: "user",
        reason: "用户完成每日 CEO 复盘。",
        changedFields: ["reviewStatus", "statementIds", "correctionId", "completedAt"],
        now,
      }),
    ]);
  });
  return { review, correction, statements };
}

export async function getCorrectionForDate(
  database: CEOOSDatabase,
  dateKey: string,
): Promise<BehaviorCorrection | undefined> {
  return (
    await database.structuredRecords
      .where("[recordType+effectiveDate]")
      .equals(["behavior_correction", dateKey])
      .toArray()
  )
    .filter(
      (record): record is BehaviorCorrection =>
        isCorrection(record) && record.deletionState === "active",
    )
    .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt))[0];
}

export async function verifyCorrection(
  database: CEOOSDatabase,
  correctionId: string,
  result: CorrectionVerificationResult,
  details: {
    evidence?: string;
    explanation?: string;
    learning?: string;
    continueNeeded: boolean;
  },
  now = new Date().toISOString(),
): Promise<BehaviorCorrection> {
  const existing = await database.structuredRecords.get(correctionId);
  if (!existing || !isCorrection(existing)) {
    throw new Error("CEO-CORRECTION-001: 找不到待验证的行为纠偏。");
  }
  const correctionStatus =
    result === "completed"
      ? ("completed" as const)
      : result === "unreasonable"
        ? ("cancelled" as const)
        : ("not_completed" as const);
  const updated: BehaviorCorrection = {
    ...existing,
    status: correctionStatus === "completed" ? "completed" : "confirmed",
    correctionStatus,
    verification: { result, ...details, verifiedAt: now },
    updatedAt: now,
  };
  await database.transaction("rw", database.structuredRecords, database.auditEvents, async () => {
    await database.structuredRecords.put(updated);
    await database.auditEvents.put(
      createAuditEvent({
        entityType: "behavior_correction",
        entityId: updated.id,
        action: "update",
        actor: "user",
        reason: "用户记录前一日行为纠偏的验证结果；系统未自动滚动。",
        changedFields: ["correctionStatus", "verification"],
        now,
      }),
    );
  });
  return updated;
}

export async function getReviewHistory(
  database: CEOOSDatabase,
  limit = 30,
): Promise<ReviewHistoryItem[]> {
  const reviews = (
    await database.structuredRecords.where("recordType").equals("daily_review").toArray()
  )
    .filter(
      (record): record is DailyCEOReview =>
        isDailyReview(record) &&
        record.deletionState === "active" &&
        (record.reviewStatus === "completed" || record.reviewStatus === "exported"),
    )
    .sort((left, right) => right.reviewDate.localeCompare(left.reviewDate))
    .slice(0, limit);
  const corrections = (
    await database.structuredRecords.where("recordType").equals("behavior_correction").toArray()
  ).filter(isCorrection);
  const summaries = await database.externalSummaries.toArray();
  return reviews.map((review) => ({
    review,
    correction: corrections.find((correction) => correction.id === review.correctionId),
    exported: summaries.some((summary) => summary.payload.review_id === review.id),
  }));
}

export async function createReviewExportPreview(
  database: CEOOSDatabase,
  review: DailyCEOReview,
  includeFields: ReviewDeviationField[],
  now = new Date().toISOString(),
): Promise<{
  envelope: CEOReviewExport;
  summary: ExternalSummaryRecord;
  draft: BoardSummaryDraft;
}> {
  if (!review.correctionId) throw new Error("CEO-EXPORT-001: 复盘缺少已确认纠偏。");
  const fields = Object.fromEntries(
    Object.entries(review.exportFieldSummaries ?? {}).map(([field, summary]) => [
      field,
      summary ? { structuredRecordId: review.id, summary } : null,
    ]),
  ) as BoardSummaryDraft["fields"];
  const draft: BoardSummaryDraft = {
    reviewId: review.id,
    date: review.reviewDate,
    fields,
    correctionId: review.correctionId,
    confidence: 0.75,
    includeFields,
  };
  const records = await database.structuredRecords.toArray();
  const projected = await projectCEOReviewExport(draft, records, now);
  return { ...projected, draft };
}

export async function confirmReviewExport(
  database: CEOOSDatabase,
  review: DailyCEOReview,
  includeFields: ReviewDeviationField[],
  now = new Date().toISOString(),
): Promise<CEOReviewExport> {
  const { envelope, summary } = await createReviewExportPreview(
    database,
    review,
    includeFields,
    now,
  );
  const manifest: DataExportManifest = {
    id: crypto.randomUUID(),
    schemaVersion: INTERNAL_SCHEMA_VERSION,
    createdAt: now,
    exportType: "board_summary",
    status: "completed",
    includedRecordIds: summary.sourceStructuredRecordIds,
    excludedFields: (
      [
        "uncomfortable_truth",
        "focus_deviation",
        "repetition_failure",
        "trust_deviation",
        "decision_quality_issue",
        "delegation_issue",
        "risk_issue",
        "energy_issue",
        "field_adaptation_issue",
      ] satisfies ReviewDeviationField[]
    ).filter((field) => !includeFields.includes(field)),
    payloadHash: envelope.payload_hash,
    sensitive: false,
    warningAcknowledged: true,
  };
  const updatedReview: DailyCEOReview = {
    ...review,
    reviewStatus: "exported",
    exportedAt: now,
    updatedAt: now,
  };
  await database.transaction(
    "rw",
    database.externalSummaries,
    database.exportManifests,
    database.structuredRecords,
    database.auditEvents,
    async () => {
      await database.externalSummaries.put(summary);
      await database.exportManifests.put(manifest);
      await database.structuredRecords.put(updatedReview);
      await database.auditEvents.put(
        createAuditEvent({
          entityType: "CEOReviewExport",
          entityId: envelope.contract_id,
          action: "export",
          actor: "user",
          reason: "用户逐项预览并确认董事会最小摘要。",
          changedFields: includeFields,
          now,
        }),
      );
    },
  );
  return envelope;
}
